<!-- 
Chart showing errors over the last week
-->
<template>
  <div>
    <div v-if="noToolbar">
      <bar-chart 
        v-if="!isChartEmpty(chartData)"
        ref="chart"
        :chart-data="chartData"
        :chart-options="chartOptions" 
        style="height: 265px"
      />
      <our-chart-is-empty-placeholder v-if="isChartEmpty(chartData)" style="height: 265px" />
    </div>
    <v-flex v-else>
      <v-card>
        <our-widget-toolbar 
          fixed for-dialog 
          title="Errors over the last week" 
          @refresh="refresh"
        />
        <our-busy-indicator :show="!loaded"/>
        <v-card-text class="pb-3">
          <bar-chart 
            v-if="!isChartEmpty(chartData)"
            ref="chart" 
            :chart-data="chartData" 
            :chart-options="chartOptions" 
            style="height: 265px"
          />
          <our-chart-is-empty-placeholder v-if="isChartEmpty(chartData)" style="height: 265px" />
        </v-card-text>
        <slot/>
      </v-card>
    </v-flex>
  </div>
</template>

<script>
import _ from "underscore";
import moment from "moment";
import * as formatting from "@/helpers/formatting.helper";
import * as action from "@/store/exception-logs/action-types";
import * as mutation from "@/store/exception-logs/mutation-types";
import BarChart from "@/components/common/chart/BarChart.vue";
import chartMixins from "@/mixins/chart.mixins";

export default {
  components: { BarChart },
  mixins: [chartMixins],
  props: {
    noToolbar: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        onClick: this.chartClick,
        onHover: this.chartHover,
        tooltips: {
          callbacks: {
            label: this.getTooltipLabel,
            title: this.getTooltipTitle,
            afterBody: this.getTooltipFooter
          }
        },
        scales: {
          yAxes: [{ ticks: { min: 0 }, afterBuildTicks: this.setYAxesTicks }]
        }
      };
    },
    state() {
      return this.$store.state.exceptionLogs.week;
    },
    chartData() {
      if (!this.loaded) return {};
      const labels = this.state.data.labels.map(b => b);
      return {
        labels,
        datasets: [{ data: this.state.data.values, backgroundColor: "#E57373", hoverBackgroundColor: "#E57373" }]
      };
    },
    loaded() {
      return this.state.loaded;
    }
  },
  watch: {
    loaded() {
      this.loaded ? this.drawChart() : this.refresh();
    }
  },
  mounted() {
    this.loaded ? this.drawChart() : this.refresh();
  },
  methods: {
    async refresh() {
      await this.$store.dispatch(action.STATS_FOR_WEEK);
      if (this.loaded) {
        this.drawChart();
      }
    },
    drawChart() {
      this.$nextTick(() => {
        if (this.$refs.chart) this.$refs.chart.display();
      });
    },
    chartClick: function(e) {
      const chartObject = this.$refs.chart.$data._chart;
      const activePoints = chartObject.getElementsAtXAxis(e);
      if (activePoints[0]) {
        const idx = activePoints[0]["_index"];
        const number = this.chartData.datasets[0].data[idx];
        if (number > 0) {
          const date = moment(this.state.data.dates[idx]).format("YYYY-MM-DD");
          this.$store.commit(mutation.LIST_CLEAR_CRITERIA);
          this.$store.commit(mutation.LIST_SET_CRITERIA, { fromDate: date, toDate: date, levels: ["Error", "Fatal"] });
          this.$store.dispatch(action.LIST);
          this.$router.push("/exception-logs");
          this.$emit("click");
        }
      }
    },
    chartHover: function(e) {
      const chartObject = this.$refs.chart.$data._chart;
      const activePoints = chartObject.getElementsAtXAxis(e);
      if (activePoints[0]) {
        const idx = activePoints[0]["_index"];
        const number = this.chartData.datasets[0].data[idx];
        if (number > 0) {
          chartObject.canvas.style.cursor = "pointer";
          return;
        }
      }
      chartObject.canvas.style.cursor = "default";
    },
    getTooltipLabel(tooltipItem, data) {
      const index = tooltipItem.index;
      const number = data.datasets[0].data[index];
      return `${formatting.formatNumber(number)} errors`;
    },
    getTooltipTitle(tooltipItems) {
      const index = _(tooltipItems).first().index;
      const date = moment(this.state.data.dates[index]);
      return date.format("dddd Do MMMM");
    },
    getTooltipFooter(tooltipItems) {
      const index = _(tooltipItems).first().index;
      const number = this.state.data.values[index];
      return number > 0 ? ["", "(click to show)"] : null;
    },
    setYAxesTicks(axis) {
      return _(axis.ticks).filter(t => t == Math.trunc(t));
    }
  }
};
</script>
